import React, { useEffect, useRef, useState } from "react";

import { Box, Grid } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import classnames from "classnames";
import queryString from "query-string";
import { useTranslation } from "react-i18next";
import { decodeToken, isExpired } from "react-jwt";
import { useHistory } from "react-router-dom";

import { NotificationToast } from "../../../components/Generic/Toast/NotificationToast";
import { PublicFooter } from "../../../components/PublicFooter";
import { AppConfig, PUBLIC_ROUTES } from "../../../constants";
import { changeTitlePageByResource } from "../../../helpers/helpers";
import { Decoded } from "../../../models/decoded";
import { Error } from "../../../models/error";
import UserService from "../../../services/user.service";
import ConfirmEmailAddressComponent, {
  ConfirmEmailAddressHandle,
} from "./components/Form";
import LinkExpired from "./components/LinkExpired";
import useStyles from "./styles";

const IMAGE_CONFIRM_EMAIL = require("../../../assets/img/confirm-email-check.svg");

const ConfirmEmailAddress = () => {
  const history = useHistory();

  const { t: translate } = useTranslation();

  const parsed: { token?: string } = queryString.parse(window.location.search);

  const confirmEmailAddressComponentRef = useRef<ConfirmEmailAddressHandle>(null);

  const [token, setToken] = useState<string>("");
  const [linkHasExpired, setLinkHasExpired] = useState<boolean>(false);

  const [email, setEmail] = useState<string>("");
  const [userId, setUserId] = useState<string>("");
  const [decodedUserToken, setDecodedUserToken] = useState<Decoded | null>();

  const classes = useStyles();

  const { mutate: confirmEmail, isLoading: isLoadingConfirmEmail } = useMutation(
    ["users.confirm.email"],
    (user: { id: number }) => UserService.confirmEmail(user.id),
    {
      onSuccess: () => {
        recoverPassword(confirmEmailAddressComponentRef?.current?.password ?? "");
      },
      onError: (error: AxiosError<Error>) => {
        NotificationToast.warning(
          error?.response?.data?.message || translate("thereWasAnErrorConfirmingTheEmail")
        );
        history.replace(PUBLIC_ROUTES.SIGN_IN);
      },
    }
  );

  const { mutate: recoverPassword, isLoading: isLoadingRecoverPassword } = useMutation(
    ["users.recover.password"],
    (password: string) => UserService.recoverPassword(userId, password, password),
    {
      onSuccess: (response) => {
        if (response && response.data) {
          NotificationToast.success(translate("operationPerformedSuccessfully"));
          history.replace({ pathname: PUBLIC_ROUTES.SIGN_IN });
        }
      },
      onError: () => {
        NotificationToast.error(translate("anErrorOccurredTryAgain"));
      },
    }
  );

  useEffect(() => {
    changeTitlePageByResource("confirm email");
  }, []);

  useEffect(() => {
    if (parsed?.token) {
      setToken(parsed?.token);
    } else {
      history.replace(PUBLIC_ROUTES.SIGN_IN);
    }
  }, [parsed]);

  useEffect(() => {
    if (token) {
      const decodedToken = decodeToken<Decoded>(token);
      const tokenIsExpired = isExpired(token);

      if (decodedToken && tokenIsExpired) {
        NotificationToast.error(translate("emailConfirmationTimeHasExpired"));
        setLinkHasExpired(true);
      } else if (decodedToken && decodedToken?.id && !tokenIsExpired) {
        setDecodedUserToken(decodedToken);
        setUserId(String(decodedToken?.id));
        setEmail(decodedToken?.email);
      } else {
        NotificationToast.error(translate("thereWasAnErrorConfirmingTheEmail"));
        history.replace(PUBLIC_ROUTES.SIGN_IN);
      }
    }
  }, [token]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (decodedUserToken) {
      confirmEmail({ id: decodedUserToken?.id });
    }
  };

  if (linkHasExpired) {
    return <LinkExpired token={token} />;
  }

  return (
    <div className={classes.mainDiv}>
      <Grid className={classes.container} container direction="row">
        <Grid item xs={12} sm={6}>
          <Box className={classes.leftBox}>
            <img
              src={AppConfig.appLogo}
              alt="session-logo"
              className={classnames("img-fluid", classes.image)}
            />
            <div className={classes.imageConfirmEmailContainer}>
              <img src={IMAGE_CONFIRM_EMAIL} className={classes.imageConfirmEmail} />
            </div>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box
            className={classes.rightBox}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Box width="85%">
              <ConfirmEmailAddressComponent
                ref={confirmEmailAddressComponentRef}
                email={email}
                isLoading={isLoadingConfirmEmail || isLoadingRecoverPassword}
                handleSubmit={handleSubmit}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>

      <PublicFooter />
    </div>
  );
};

export default ConfirmEmailAddress;
